// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Container,
  Header,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { Messages } from '../../Common/ErrorMessages';
import { useLocales } from '../../../utils/locales';
import { ApiError } from '../../../utils/serverErrors';
import { SubmittedState } from '../PricingModels/pricingModels';
import ScopingModelFields from './ScopingModelFields';
import * as scopingModels from './scopingModels';
import { useBaseRate } from './useLoaded';

interface ScopingModelCreateProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPost: (path: string, body: object) => Promise<unknown>;
  };
  navigate: (path: string, state: SubmittedState) => void;
  canWrite: boolean;
}

export const ScopingModelCreate = ({
  apiClient,
  navigate,
  canWrite,
}: ScopingModelCreateProps): JSX.Element | null => {
  const [values, setValues] = useState<scopingModels.FormValues>(scopingModels.emptyForm);
  const [fieldErrors, setFieldErrors] = useState<scopingModels.FieldErrors>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { locales } = useLocales(apiClient, scopingModels.describeError);
  const baseRate = useBaseRate(apiClient, canWrite);

  if (!canWrite) {
    return null;
  }

  const change = (field: scopingModels.FormField, value: string | string[]): void => {
    setValues({ ...values, [field]: value });
    setFieldErrors({ ...fieldErrors, [field]: undefined });
  };

  const submit = (): void => {
    setFormErrors([]);
    const validationErrors = scopingModels.validateForm(values, true);
    setFieldErrors(validationErrors);
    if (scopingModels.hasFieldErrors(validationErrors)) {
      return;
    }
    const request = scopingModels.toCreateRequest(values);
    setSubmitting(true);
    // Submitting ends before navigating away, so no state is set after unmount.
    apiClient
      .httpPost(scopingModels.SCOPING_MODELS_PATH, request)
      .then(response => {
        setSubmitting(false);
        const changeSet = response as scopingModels.ChangeSet;
        navigate(scopingModels.scopingModelRoute(changeSet.weightSchemeId), {
          submittedChangeSetId: changeSet.changeSetId,
        });
      })
      .catch((error: ApiError) => {
        setSubmitting(false);
        const failure = scopingModels.describeSaveError(error, request, values, locales);
        setFieldErrors(failure.fieldErrors);
        setFormErrors(failure.formErrors);
      });
  };

  return (
    <SpaceBetween size="m">
      {formErrors.length > 0 && (
        <Alert id="scopingModelFormErrors" type="error">
          <Messages messages={formErrors} />
        </Alert>
      )}
      <Container header={<Header variant="h2">Create scoping model</Header>}>
        <SpaceBetween size="m">
          <ScopingModelFields
            apiClient={apiClient}
            baseRate={baseRate}
            values={values}
            fieldErrors={fieldErrors}
            disabled={submitting}
            creating
            onChange={change}
          />
          <Button
            id="submitScopingModel"
            variant="primary"
            loading={submitting}
            disabled={submitting || baseRate.missing || !scopingModels.readyToSubmit(values)}
            onClick={submit}
          >
            Create
          </Button>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

ScopingModelCreate.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
};

const ScopingModelCreateView = (
  props: Omit<ScopingModelCreateProps, 'apiClient'>
): JSX.Element | null => <ScopingModelCreate apiClient={ApiClient} {...props} />;

export default ScopingModelCreateView;
